import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { RatingType } from 'app/types/loan';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { createTheme as createDSTheme } from 'app/shopify';

const theme = createDSTheme();

const RATING_VALUES = [1, 2, 3, 4, 5] as const;

const ORDERED_TYPES: RatingType[] = [
  RatingType.AssetQuality,
  RatingType.LogisticsReliability,
  RatingType.CommunityTrust,
];

type QuestionTone = 'neutral' | 'positive' | 'negative' | 'medium';

const questionCardStyle = (borderColor: string) => ({
  backgroundColor: theme.colors.white,
  borderColor,
  borderRadius: 18,
  borderWidth: 1,
  padding: theme.spacing['spacing.general.x4'],
});

const starButtonPressedStyle = {
  opacity: 0.82,
  transform: [{ scale: 0.96 }],
} as const;

const getToneFromValue = (value?: number): QuestionTone => {
  if (typeof value !== 'number') return 'neutral';
  if (value <= 2) return 'negative';
  if (value === 3) return 'medium';
  if (value >= 4) return 'positive';
  return 'neutral';
};

const getToneColors = (tone: QuestionTone) => {
  if (tone === 'positive') {
    return {
      accent: theme.colors['text.emphasized'],
      accentSoft: theme.colors['surface.tertiary'],
      chipBackground: theme.colors['surface.secondary'],
    };
  }

  if (tone === 'negative') {
    return {
      accent: theme.colors['surface.error.default'],
      accentSoft: theme.colors['surface.error.subtle'],
      chipBackground: theme.colors['surface.error.subtle'],
    };
  }

  if (tone === 'medium') {
    return {
      accent: theme.colors['text.warning'],
      accentSoft: theme.colors['surface.warning.subtle'],
      chipBackground: theme.colors['surface.brand.secondary-3.subtle'],
    };
  }

  return {
    accent: theme.colors['border.secondary'],
    accentSoft: theme.colors['surface.secondary-neutral'],
    chipBackground: theme.colors['surface.secondary-neutral'],
  };
};

function getQuestionCopy(t: ReturnType<typeof useTranslation>['t'], type: RatingType, isLender: boolean) {
  const role = isLender ? 'lender' : 'borrower';
  const questionKey = getQuestionKey(type);

  return t(`newRating.figmaQuestions.${role}.${questionKey}.question`);
}

function getQuestionKey(type: RatingType): 'assetQuality' | 'logisticsReliability' | 'communityTrust' {
  switch (type) {
    case RatingType.AssetQuality:
      return 'assetQuality';
    case RatingType.LogisticsReliability:
      return 'logisticsReliability';
    case RatingType.CommunityTrust:
      return 'communityTrust';
  }
}

function getQuestionBadge(
  t: ReturnType<typeof useTranslation>['t'],
  type: RatingType,
  isLender: boolean,
  value?: number,
) {
  const tone = getToneFromValue(value);
  const role = isLender ? 'lender' : 'borrower';

  switch (type) {
    case RatingType.AssetQuality:
      if (tone === 'negative') {
        return {
          emoji: '😬',
          label: t(`newRating.badges.${role}.assetQuality.negative`),
          tone,
        };
      }

      return {
        emoji: '✨',
        label: t(`newRating.badges.${role}.assetQuality.${tone === 'positive' ? 'positive' : 'neutral'}`),
        tone,
      };
    case RatingType.LogisticsReliability:
      return {
        emoji: tone === 'negative' ? '😬' : '⏳',
        label: t(`newRating.badges.${role}.logisticsReliability.${tone}`),
        tone,
      };
    case RatingType.CommunityTrust:
      return {
        emoji: tone === 'negative' ? '😬' : '🫶',
        label: t(`newRating.badges.${role}.communityTrust.${tone}`),
        tone,
      };
  }
}

function getRatingSummary(
  t: ReturnType<typeof useTranslation>['t'],
  type: RatingType,
  isLender: boolean,
  value?: number,
) {
  if (typeof value !== 'number') return '';

  const role = isLender ? 'lender' : 'borrower';
  const questionKey = getQuestionKey(type);
  return t(`newRating.figmaQuestions.${role}.${questionKey}.labels.${String(value)}`);
}

interface RatingQuestionProps {
  index: number;
  type: RatingType;
  isLender: boolean;
  value: number | undefined;
  onChange: (value: number) => void;
}

export function RatingQuestion({ type, isLender, index, value, onChange }: RatingQuestionProps) {
  const { t } = useTranslation('ratings');
  const role = isLender ? 'lender' : 'borrower';
  const selected = typeof value === 'number';
  const question = getQuestionCopy(t, type, isLender);
  const badge = getQuestionBadge(t, type, isLender, value);
  const toneColors = getToneColors(badge.tone);
  const activeLabel = getRatingSummary(t, type, isLender, value);
  const stepLabel = t('newRating.figmaStepCounter', {
    current: Math.min(index + 1, ORDERED_TYPES.length),
    total: ORDERED_TYPES.length,
  });

  return (
    <Animated.View style={questionCardStyle(toneColors.accent)}>
      <Column gap="spacing.general.x4">
        <Row alignItems="center" justifyContent="space-between">
          <View
            style={{
              backgroundColor: toneColors.chipBackground,
              borderRadius: 999,
              paddingHorizontal: theme.spacing['spacing.general.x3'],
              paddingVertical: 6,
            }}
          >
            <Row alignItems="center" gap="spacing.general.x1">
              <Text fontSize={13}>{badge.emoji}</Text>
              <Text color="text.primary" fontSize={12} fontWeight="700">
                {badge.label}
              </Text>
            </Row>
          </View>

          <Text color="text.secondary" fontSize={14} fontWeight="500">
            {stepLabel}
          </Text>
        </Row>

        <Text color="text.primary" fontSize={20} fontWeight="700" lineHeight={28}>
          {question}
        </Text>

        <Box paddingTop="spacing.general.x2">
          <Row gap="spacing.general.x4" justifyContent="center">
            {RATING_VALUES.map((ratingValue) => {
              const choiceSelected = ratingValue <= (value ?? 0);
              const iconColor = !choiceSelected
                ? getToneColors('neutral').accent
                : value && value <= 2
                  ? theme.colors['surface.error.default']
                  : value === 3
                    ? theme.colors['text.warning']
                    : theme.colors['text.emphasized'];

              return (
                <Pressable
                  accessibilityLabel={t(
                    `newRating.figmaQuestions.${role}.${getQuestionKey(type)}.labels.${String(ratingValue)}`,
                  )}
                  accessibilityRole="button"
                  accessibilityState={{ selected: value === ratingValue }}
                  hitSlop={6}
                  key={ratingValue}
                  onPress={() => onChange(ratingValue)}
                  style={({ pressed }) => [pressed && starButtonPressedStyle]}
                >
                  <Column alignItems="center" justifyContent="center">
                    <MaterialCommunityIcons
                      color={iconColor}
                      name={choiceSelected ? 'star' : 'star-outline'}
                      size={38}
                    />
                  </Column>
                </Pressable>
              );
            })}
          </Row>
        </Box>

        {selected ? (
          <Text color="text.primary" fontSize={15} fontWeight="700" textAlign="center">
            {activeLabel}
          </Text>
        ) : null}
      </Column>
    </Animated.View>
  );
}
